import React, { useEffect, useState, useCallback, useMemo } from 'react';
import "./CreateOTSFleetEvent.scss";
import CancelModal from "../Modal/CancelModal";
import { useHistory } from "react-router-dom";
import { useAuthentication } from "meportal/AuthenticationProvider";
import OTSFleetEventDetails from "./OTSFleetEventDetails/OTSFleetEventDetails";
import FleetDetails from "./FleetDetails/FleetDetails";
import ConfirmModal from '../Modal/ConfirmModal';
import { InlineNotificationProvider } from "../../providers/InlineNotificationProvider";
import { createOTSEvent, updateOtsEvent } from "../../services/otsEventService";
import { useDispatch } from "react-redux";
import { addEvent, updateEvent } from "../../store/feature/OpenEventsListSlice";
import { findSubAction, isAirborneEvent, deriveMessageTitle, deriveProblemStatementHistory, deriveSubActionForEventChange } from '../EditOtsForm/UpdateSubAction';
import { debounce } from 'lodash';
import LoadingEllipsis from '../../helpers/loadingEllipsis';
import { GreenCheckMarkWithStatus, TryAgain, WarningWithStatus } from '../../constants/SvgIcons';
import { AircraftData, OtsFleetDetailsData } from '../../Models/CreateFleetOts';
import { useConversationClient } from "meconversation/ConversationClientProvider";
import { conversationAddMessage } from "meconversation/ConversationWrapperService";
import { OtsAircraft } from '../../Models/OtsAircraft';
import { EditOtsData } from '../../Models/EditOtsData';
import { useInlineNotification } from '../../providers/InlineNotificationProvider'; 
import pLimit from 'p-limit';
import { INLINE_NOTIFICATION_TYPES } from '../InlineNotification/inlineNotificationConstants';
import { logInformation, logError } from "meportal/Logger";
import { APP_NAME_APPINSIGHTS } from '../../constants/Constants';
import { getDatetimeUtcNowString } from '../../helpers/DateTime';

interface CreateOTSFleetEventProps {
  handledata: (data: any) => void;
}

const CreateOTSFleetEvent: React.FC<CreateOTSFleetEventProps> = ({ handledata }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { userData, getLatestAccessToken } = useAuthentication();
  const { twilioClient } = useConversationClient();
  const [status, setStatus] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModalBeforePost, setShowCancelModalBeforePost] = useState(false);
  const [selectedFleetType, setSelectedFleetType] = useState<string | null>(null);
  const [otsFleetDetailsData, setOtsFleetDetailsData] = useState<OtsFleetDetailsData | null>(null);
  const [isOtsFleetDetailsValid, setIsOtsFleetDetailsValid] = useState(false);
  const [combinedAircraftData, setCombinedAircraftData] = useState<AircraftData[]>([]);
  const [fleetAircraftData, setFleetAircraftData] = useState<AircraftData[][]>([]);
  const [existingOtsEvents, setExistingOtsEvents] = useState<OtsAircraft[]>([]);
  const [isValidDetailsCountShowing, setIsValidDetailsCountShowing] = useState(false);
  const [twilioClientReady, setTwilioClientReady] = useState(false);
  const [successEvents, setSuccessEvents] = useState<OtsEvent[]>([]);
  const [successUpdateEvents, setSuccessUpdateEvents] = useState<UpdatedOtsEvent[]>([]);
  const [failedEvents, setFailedEvents] = useState<OtsEvent[]>([]);
  const [failedUpdateEvents, setFailedUpdateEvents] = useState<UpdatedOtsEvent[]>([]);
  const [twilioDataFailedUpdateEvents, setTwilioDataFailedUpdateEvents] = useState<any[]>([]);
  const [overlay, setOverlay] = React.useState(false);
  const moduleName = `${APP_NAME_APPINSIGHTS}-CreateOTSFleetEvent`;
  
  const eventCreatedTitle = "Event created";
  const failedStatus = "Failed";
  const createdStatus = "Created";
  const movedStatus = "Moved";
  const { addInlineNotification, removeInlineNotification } = useInlineNotification();
  const successTitle = 'events created successfully';
  const failureTitle = 'events failed to create';
  const totalAttempts = 3;
  const [attempts, setAttempts] = useState(0);
  const failureDescription = `You have ${totalAttempts - attempts}/${totalAttempts} remaining attempts to retry creating the failed events`;
  const failureDescriptionUpdate = `You have reached the maximum retry attempts. Please exit this screen and create the remaining failed events individually.`;

  useEffect(() => {
    if (twilioClient) {
      setTwilioClientReady(true);
    }
    else {
      setTwilioClientReady(false);
    }
  }, [twilioClient]);

  const isSubmitDisabled = useMemo(() => {
    return !(isOtsFleetDetailsValid && isValidDetailsCountShowing && twilioClientReady);
  }, [isOtsFleetDetailsValid, isValidDetailsCountShowing, twilioClientReady]);

  const debouncedHandleData = useCallback(
    debounce((data: any) => {
      handledata(data);
    }, 300),
    [handledata]
  );

  const handleOtsFleetDetailsChange = useCallback((isValid: boolean, data: OtsFleetDetailsData) => {
    setIsOtsFleetDetailsValid(isValid);
    setOtsFleetDetailsData(data);
  }, []);

  const handleValidDetailsCountShowing = useCallback((isValidDetailResults: boolean) => {
    setIsValidDetailsCountShowing(isValidDetailResults);
  }, []);

  const handleAddSubmit = useCallback(() => {
    setShowConfirmModal(true);
  }, []);


  const handleDone = () => {
    //show confirmation on failure else don't
    if(failedEvents.length > 0 || failedUpdateEvents.length > 0)
    {  
      setShowCancelModal(true);
    }
    else{
      history.push('/open-events');
    }
};


  const handleClose = useCallback(() => {
    setShowCancelModalBeforePost(true);
  }, []);

  const handleKeepEditing = useCallback(() => {
    setShowCancelModalBeforePost(false);
    setOverlay(false);
  }, []);

  const handleKeepOnFleet = useCallback(() => {
    setShowCancelModal(false);
  }, []);

  const handleCancelEvent = useCallback(() => {
    history.push('/open-events');
  }, [history]);

  const handleFleetTypeChange = useCallback((fleetType: string) => {
    setSelectedFleetType(null);
    setSelectedFleetType(fleetType);
  }, []);

  const handleConfirmClose = useCallback(() => {
    setShowConfirmModal(false);
    setOverlay(false);
  }, []);

  const handleDataUpdate = useCallback((data: AircraftData[][], openEvents: OtsAircraft[]) => {
    if (data != null) {
      setFleetAircraftData(data);
      const combinedData = [
        ...data[0], // Out aircraft data
        ...data[1], // Airborne data
        ...data[2],  // In aircraft data
        // data[3] is OTS Aircraft data
      ];
      setCombinedAircraftData(combinedData);
      setExistingOtsEvents(openEvents);
    }
  }, []);

  useEffect(() => {
    debouncedHandleData({
      status,
      isSubmitDisabled,
      handleClose,
      handleAddSubmit,
      handleDone
    });
  }, [isSubmitDisabled, status, debouncedHandleData, handleClose, handleAddSubmit]);

  useEffect(() =>{
    const totalEvents = combinedAircraftData.length + existingOtsEvents.length;
    const totalProcessedEvents = successEvents.length + successUpdateEvents.length + failedEvents.length + failedUpdateEvents.length;
    if (totalProcessedEvents > 0 && totalEvents === totalProcessedEvents) {
      const failureCount = failedEvents.length + failedUpdateEvents.length;
      const successCount = successEvents.length + successUpdateEvents.length;
      if (failureCount > 0) {
        const failureStatement = `${failureCount}/${totalEvents} ${failureTitle}`;
        addInlineNotification(INLINE_NOTIFICATION_TYPES.WARNING, failureStatement, failureDescription, failureDescriptionUpdate, handleTryAttempts, attempts);
      } else {
        const successStatement = `${successCount}/${totalEvents} ${successTitle}`;
        addInlineNotification(INLINE_NOTIFICATION_TYPES.SUCCESS, successStatement, "");
      }
      setStatus("done");
    }
  }, [successEvents, failedEvents, successUpdateEvents, failedUpdateEvents]);
  

  const handleConfirmAction = async () => {
    setStatus("loading");
    setOverlay(true);
    setShowConfirmModal(false);
    const BATCH_SIZE = 3; // Number of concurrent API calls
    const currentDateTime = new Date().toISOString();
    const limit = pLimit(BATCH_SIZE);

    const processCreateItem = async (data: AircraftData): Promise<boolean> => {
      const updatedOtsEventData = mapOtsCreateData(data, userData.profile.company, currentDateTime, otsFleetDetailsData?.message);
      const statusCode = await createOTSEvent(userData, updatedOtsEventData);
      if (statusCode === 200) {
        const formattedOutOfServiceTime = updatedOtsEventData.outOfServiceTime.slice(0, 16);
        const uniqueConversationKey = `${updatedOtsEventData.tail}:${formattedOutOfServiceTime}`;
        const twilioResponse = await conversationAddMessage(true, uniqueConversationKey, twilioClient, userData, otsFleetDetailsData?.filesArray, otsFleetDetailsData?.message, eventCreatedTitle);
        if(twilioResponse.success){
          logInformation(
            `${getDatetimeUtcNowString("YYYY-MM-DD HH:mm:ss")}Z => Success conversation create and message add conversation UI. Twilio response: ${JSON.stringify(twilioResponse)} User: ${userData?.profile?.email}`,
            moduleName,
            "processCreateItem"
          );
          //success create status
          setTimeout(() => {
          setSuccessEvents(prevEvents => [...prevEvents, updatedOtsEventData]);
          setFleetAircraftData(prevData => {
            let updated = false;
            return prevData.map(subArray => {
              if (updated) return subArray;
              return subArray.map(item => {
                if (!updated && item.aircraft === data.aircraft) {
                  updated = true;
                  return { ...item, status: <GreenCheckMarkWithStatus status={createdStatus} /> };
                }
                return item;
              });
            });
          });
          }, 2000);
          dispatch(addEvent(updatedOtsEventData));
          return true; // Indicate success
        } else {
          setFailedEvents(prevEvents => [...prevEvents, updatedOtsEventData]);
          logError(
            `${getDatetimeUtcNowString("YYYY-MM-DD HH:mm:ss")}Z => Failed conversation create or message add conversation UI. Twilio response: ${JSON.stringify(twilioResponse)} User: ${userData?.profile?.email}`,
            moduleName,
            "processCreateItem"
          );
          // Update status to failure
          setFleetAircraftData(prevData => {
            let updated = false;
            return prevData.map(subArray => {
              if (updated) return subArray;
              return subArray.map(item => {
                if (!updated && item.aircraft === data.aircraft) {
                  updated = true;
                  return { ...item, status: <WarningWithStatus status={failedStatus} /> };
                }
                return item;
              });
            });
          });
          return false; // Indicate failure
        } 

      } else {
        setFailedEvents(prevEvents => [...prevEvents, updatedOtsEventData]);
        setFleetAircraftData(prevData => {
          let updated = false;
          return prevData.map(subArray => {
            if (updated) return subArray;
            return subArray.map(item => {
              if (!updated && item.aircraft === data.aircraft) {
                updated = true;
                return { ...item, status: <WarningWithStatus status={failedStatus} /> };
              }
              return item;
            });
          });
        });
        return false; // Indicate failure
      }
    };
    const processUpdateMoveItem = async (data: OtsAircraft, fleetUpdates: OtsFleetDetailsData) => {
      let editData: EditOtsData = {
        station: data.Station,
        location: data.Location,
        advtime: fleetUpdates.advTime ? new Date(fleetUpdates.advTime) : undefined,
        etrtime: undefined,
        ataCode: fleetUpdates.ataCode.toString(),
        ataCodeDescription: fleetUpdates.ataCodeDescription,
        otstime: data.OutOfServiceDateTimeUTC ? new Date(data.OutOfServiceDateTimeUTC) : undefined,
        problemText: fleetUpdates.problemStatement,
        isSubmitDisabled: false
      }
      const updatedOtsEventData = mapOtsUpdateData(data, editData, fleetUpdates.eventType ?? "" , fleetUpdates.eventTypeCategory, userData.profile.company, currentDateTime);
      const resp = await updateOtsEvent(userData, updatedOtsEventData);
      const eventUpdatedTitle = deriveMessageTitle(updatedOtsEventData.subAction, updatedOtsEventData, data);
      const uniqueConversationKey = data.ConversationUniqueKey;
      const twilioData = {eventUpdatedTitle, uniqueConversationKey};

      if (resp && resp.statusCode === 200) {
        const twilioResponse = await conversationAddMessage(false, uniqueConversationKey, twilioClient, userData, otsFleetDetailsData?.filesArray, otsFleetDetailsData?.message, eventUpdatedTitle);
        if(twilioResponse.success){
          logInformation(
            `${getDatetimeUtcNowString("YYYY-MM-DD HH:mm:ss")}Z => Success message add conversation UI. Twilio response: ${JSON.stringify(twilioResponse)} User: ${userData?.profile?.email}`,
            moduleName,
            "processUpdateMoveItem"
          );
          setTimeout(() => {
          setSuccessUpdateEvents(prevEvents => [...prevEvents, updatedOtsEventData]);
          // update status to success
          setFleetAircraftData(prevData => {
            let updated = false;
            return prevData.map(subArray => {
              if (updated) return subArray;
              return subArray.map(item => {
                if (!updated && item.aircraft === data.AircraftRegistration) {
                  updated = true;
                  return { ...item, status: <GreenCheckMarkWithStatus status={movedStatus} /> };
                }
                return item;
              });
            });
          });
          }, 2000);
          dispatch(updateEvent(updatedOtsEventData));
          return true; 
        } else {
          setFailedUpdateEvents(prevEvents => [...prevEvents, updatedOtsEventData]);
          setTwilioDataFailedUpdateEvents(prevEvents => [...prevEvents, twilioData]);
          logError(
            `${getDatetimeUtcNowString("YYYY-MM-DD HH:mm:ss")}Z => Failed message add conversation UI. Twilio response: ${JSON.stringify(twilioResponse)} User: ${userData?.profile?.email}`,
            moduleName,
            "processUpdateMoveItem"
          );
          // Update status to failure
          setFleetAircraftData(prevData => {
            let updated = false;
            return prevData.map(subArray => {
              if (updated) return subArray;
              return subArray.map(item => {
                if (!updated && item.aircraft === data.AircraftRegistration) {
                  updated = true;
                  return { ...item, status: <WarningWithStatus status={failedStatus} /> };
                }
                return item;
              });
            });
          });
          return false; 
        }
      } else {
        setFailedUpdateEvents(prevEvents => [...prevEvents, updatedOtsEventData]);
        setTwilioDataFailedUpdateEvents(prevEvents => [...prevEvents, twilioData]);
        // Update status to failure
        setFleetAircraftData(prevData => {
          let updated = false;
          return prevData.map(subArray => {
            if (updated) return subArray;
            return subArray.map(item => {
              if (!updated && item.aircraft === data.AircraftRegistration) {
                updated = true;
                return { ...item, status: <WarningWithStatus status={failedStatus} /> };
              }
              return item;
            });
          });
        });
        return false; 
      }
    }

    let totalCount = 0;
    let successCount = 0;
    try {
      //set loading
      setFleetAircraftData(prevData =>
        prevData.map(subArray =>
          subArray.map(item => ({
            ...item,
            status: <LoadingEllipsis />
          }))
        )
      );
      const token = await getLatestAccessToken();
      userData.access_token = token;
      totalCount = combinedAircraftData.length + existingOtsEvents.length;
      // apply updates first 
      if (otsFleetDetailsData) {
        const promiseUpdate = existingOtsEvents.map(data => limit(() => processUpdateMoveItem(data, otsFleetDetailsData)));
        const results = await Promise.all(promiseUpdate);
        successCount += results.filter(result => result === true).length;
      }
      // create the new events 
      const promisesCreate = combinedAircraftData.map(data => limit(() => processCreateItem(data)));
      const results = await Promise.all(promisesCreate);
      successCount += results.filter(result => result === true).length;
    } catch (error) {
      console.error("Fleet OTS failed: ",error);
      const failureStatement = `${totalCount - successCount} out of ${totalCount} ${failureTitle}`;
      addInlineNotification(INLINE_NOTIFICATION_TYPES.WARNING, failureStatement, failureDescription,
        '', () => handleTryAttempts(), attempts );
      setStatus("done");
    }
  };

  const handleTryAttempts = async (): Promise<void> => {
    removeInlineNotification();
    setStatus("loading");
    setAttempts(prevValue => prevValue + 1);
    const BATCH_SIZE = 3; // Number of concurrent API calls
    const limit = pLimit(BATCH_SIZE);
    let totalCount = combinedAircraftData.length + existingOtsEvents.length;
    let successCount = successEvents.length + successUpdateEvents.length;

    const processFailedCreateItem = async (data: OtsEvent) => {
      const statusCode = await createOTSEvent(userData, data);
      const aircraftRegistrationMinusPrefixN = data.tail.replace(/^N/, '');
      if (statusCode === 200) {
        const formattedOutOfServiceTime = data.outOfServiceTime.slice(0, 16);
        const uniqueConversationKey = `${data.tail}:${formattedOutOfServiceTime}`;
        const twilioResponse = await conversationAddMessage(true, uniqueConversationKey, twilioClient, userData, otsFleetDetailsData?.filesArray, otsFleetDetailsData?.message, eventCreatedTitle);
        if(twilioResponse.success){
          logInformation(
            `${getDatetimeUtcNowString("YYYY-MM-DD HH:mm:ss")}Z => Success conversation and message add conversation UI. Twilio response: ${JSON.stringify(twilioResponse)} User: ${userData?.profile?.email}`,
            moduleName,
            "processFailedCreateItem"
          );
          //success create status
          setTimeout(() => {
          setSuccessEvents(prevEvents => [...prevEvents, data]);
          setFleetAircraftData(prevData => {
            let updated = false;
            return prevData.map(subArray => {
              if (updated) return subArray;
              return subArray.map(item => {
                if (!updated && item.aircraft === aircraftRegistrationMinusPrefixN) {
                  updated = true;
                  return { ...item, status: <GreenCheckMarkWithStatus status={createdStatus} /> };
                }
                return item;
              });
            });
          });
          }, 2000);
          dispatch(addEvent(data));
          return true; // Indicate success
        } else {
          setFailedEvents(prevEvents => [...prevEvents, data]);
          logError(
            `${getDatetimeUtcNowString("YYYY-MM-DD HH:mm:ss")}Z => Failed conversation create or message add conversation UI. Twilio response: ${JSON.stringify(twilioResponse)} User: ${userData?.profile?.email}`,
            moduleName,
            "processFailedCreateItem"
          );
          // Update status to failure
          setFleetAircraftData(prevData => {
            let updated = false;
            return prevData.map(subArray => {
              if (updated) return subArray;
              return subArray.map(item => {
                if (!updated && item.aircraft === aircraftRegistrationMinusPrefixN) {
                  updated = true;
                  return { ...item, status: <WarningWithStatus status={failedStatus} /> };
                }
                return item;
              });
            });
          });
          return false; // Indicate failure
        } 
      } else {
        setFailedEvents(prevEvents => [...prevEvents, data]);
        setFleetAircraftData(prevData => {
          let updated = false;
          return prevData.map(subArray => {
            if (updated) return subArray;
            return subArray.map(item => {
              if (!updated && item.aircraft === aircraftRegistrationMinusPrefixN) {
                updated = true;
                return { ...item, status: <WarningWithStatus status={failedStatus} /> };
              }
              return item;
            });
          });
        });
        return false; // Indicate failure
      }
    };

    const processFailedUpdateMoveItem = async (data: UpdatedOtsEvent) => {
      const resp = await updateOtsEvent(userData, data);
      const aircraftRegistrationMinusPrefixN = data.aircraftRegistration.replace(/^N/, '');
      if (resp && resp.statusCode === 200) {
        const matchingTwilioData = twilioDataFailedUpdateEvents.find(
          twilioData => twilioData.uniqueConversationKey === data.conversationUniqueKey
        );
        const uniqueConversationKey = matchingTwilioData ? matchingTwilioData.uniqueConversationKey : '';
        const eventUpdatedTitle = matchingTwilioData ? matchingTwilioData.eventUpdatedTitle : '';
        const twilioResponse = await conversationAddMessage(false, uniqueConversationKey, twilioClient, userData, otsFleetDetailsData?.filesArray, otsFleetDetailsData?.message, eventUpdatedTitle);
        if(twilioResponse.success){
          setTimeout(() => {
          setSuccessUpdateEvents(prevEvents => [...prevEvents, data]);
          logInformation(
            `${getDatetimeUtcNowString("YYYY-MM-DD HH:mm:ss")}Z => Success message add conversation UI. Twilio response: ${JSON.stringify(twilioResponse)} User: ${userData?.profile?.email}`,
            moduleName,
            "processFailedUpdateMoveItem"
          );
          // update status to success
          setFleetAircraftData(prevData => {
            let updated = false;
            return prevData.map(subArray => {
              if (updated) return subArray;
              return subArray.map(item => {
                if (!updated && item.aircraft === aircraftRegistrationMinusPrefixN) {
                  updated = true;
                  return { ...item, status: <GreenCheckMarkWithStatus status={movedStatus} /> };
                }
                return item;
              });
            });
          });
          }, 2000);
          dispatch(updateEvent(data));
          return true; 
        } else {
          setFailedUpdateEvents(prevEvents => [...prevEvents, data]);
          logError(
            `${getDatetimeUtcNowString("YYYY-MM-DD HH:mm:ss")}Z => Failed message add conversation UI. Twilio response: ${JSON.stringify(twilioResponse)} User: ${userData?.profile?.email}`,
            moduleName,
            "processFailedUpdateMoveItem"
          );
          // Update status to failure
          setFleetAircraftData(prevData => {
            let updated = false;
            return prevData.map(subArray => {
              if (updated) return subArray;
              return subArray.map(item => {
                if (!updated && item.aircraft === aircraftRegistrationMinusPrefixN) {
                  updated = true;
                  return { ...item, status: <WarningWithStatus status={failedStatus} /> };
                }
                return item;
              });
            });
          });
          return false; 
        }
      } else {
        setFailedUpdateEvents(prevEvents => [...prevEvents, data]);
        // Update status to failure
        setFleetAircraftData(prevData => {
          let updated = false;
          return prevData.map(subArray => {
            if (updated) return subArray;
            return subArray.map(item => {
              if (!updated && item.aircraft === aircraftRegistrationMinusPrefixN) {
                updated = true;
                return { ...item, status: <WarningWithStatus status={failedStatus} /> };
              }
              return item;
            });
          });
        });
        return false; 
      }
    }

    try{
      const failedUpdates = [...failedUpdateEvents];
      const failedCreates = [...failedEvents];
      setFailedUpdateEvents([]);
      setFailedEvents([]);
      //set loading for failed aircrafts
      setFleetAircraftData(prevData =>
        prevData.map(subArray =>
          subArray.map(item => {
            const isFailedAircraft = failedUpdates.some(failedEvent => 
              item.aircraft === failedEvent.aircraftRegistration.replace(/^N/, '')
            ) || failedCreates.some(failedEvent => 
              item.aircraft === failedEvent.tail.replace(/^N/, '')
            );
            return {
              ...item,
              status: isFailedAircraft ? <LoadingEllipsis /> : item.status
            };
          })
        )
      );
      const token = await getLatestAccessToken();
      userData.access_token = token;
      //calling update for failed
      const promiseFailedUpdate = failedUpdates.map(data => limit(() => processFailedUpdateMoveItem(data)));
      const updateResults = await Promise.all(promiseFailedUpdate);
      successCount += updateResults.filter(result => result === true).length;

      //calling create for failed  
      const promisesCreate = failedCreates.map(data => limit(() => processFailedCreateItem(data)));
      const createResults = await Promise.all(promisesCreate);
      successCount += createResults.filter(result => result === true).length;
    }
    catch (error){
      console.error("Fleet OTS retry failed: ",error);
      const failureStatement = `${totalCount - successCount} out of ${totalCount} ${failureTitle}`;
      addInlineNotification(INLINE_NOTIFICATION_TYPES.WARNING, failureStatement, failureDescription,
        '', () => handleTryAttempts(), attempts );
      setStatus("done");
    }
    return Promise.resolve();
  };

  const mapOtsCreateData = (createOtsFleetData: AircraftData, userCompany: string, currentDateTime: string, conversationComment: string | undefined): OtsEvent => {
    let airborneEventName = "";
    let airborneEventCategoryName = "";
    if (userCompany !== "QX" && createOtsFleetData.airborneEvent) {
      airborneEventName = createOtsFleetData.airborneEvent.Name;
      airborneEventCategoryName = createOtsFleetData.airborneEvent.Category;
    }
    return {
      action: "ADD",
      subAction: "",
      tail: "N" + createOtsFleetData.aircraft,
      airline: userCompany,
      station: createOtsFleetData.station,
      location: airborneEventName ? airborneEventName : createOtsFleetData.location ?? "",
      problemText: otsFleetDetailsData?.problemStatement ?? "",
      isEtopsCapable: createOtsFleetData.isEtopsCapable.toString(),
      eventTypeTo: airborneEventName ? (otsFleetDetailsData?.eventType ?? "") : "",
      eventTypeCategoryTo: airborneEventCategoryName ? (otsFleetDetailsData?.eventTypeCategory ?? "") : "",
      eventType: airborneEventName || (otsFleetDetailsData?.eventType ?? ""),
      eventTypeCategory: airborneEventCategoryName || (otsFleetDetailsData?.eventTypeCategory ?? ""),
      adviseTime: otsFleetDetailsData?.advTime?.toISOString(),
      etrTime: null,
      outOfServiceTime: currentDateTime,
      ATACode: otsFleetDetailsData?.ataCode ?? "",
      ataCodeDescription: otsFleetDetailsData?.ataCodeDescription ?? "",
      HasPublicAttachment: otsFleetDetailsData?.hasPublicAttachment ?? false,
      conversationPublicComment: conversationComment ?? "",
      lastUpdatedBy: userData?.profile?.name ?? "",
      isSubmitDisabled: false
    };
  };
  const mapOtsUpdateData = (existingOtsFleetData: OtsAircraft, editOtsData: EditOtsData, fleetEventType: string, fleetEventTypeCategory: string, userCompany: string, currentDateTime: string) : UpdatedOtsEvent => {
    let isAirborne: boolean = isAirborneEvent(existingOtsFleetData?.InboundFlight ?? undefined);
    let oldEventType = existingOtsFleetData.EventType;
    let oldEventTypeCategory = existingOtsFleetData.EventTypeCategory;
    
    return {
      action: "UPDATE",
      id: existingOtsFleetData.id,
      conversationUniqueKey: existingOtsFleetData.ConversationUniqueKey,
      subAction: deriveSubActionForEventChange(isAirborne, existingOtsFleetData, fleetEventType) + findSubAction(existingOtsFleetData, editOtsData), // comma delimited list of changes to the event
      aircraftRegistration: "N" + existingOtsFleetData.AircraftRegistration,
      airline: userCompany,
      station: existingOtsFleetData.Station,
      location: existingOtsFleetData.Location,
      problemText: otsFleetDetailsData?.problemStatement ?? "",
      isEtopsCapable: existingOtsFleetData.IsEtopsCapable.toString(),
      eventType: isAirborne && userCompany !== 'QX' ? oldEventType : fleetEventType,
      eventTypeCategory: isAirborne && userCompany !== 'QX' ? oldEventTypeCategory : fleetEventTypeCategory,
      eventTypeTo: isAirborne && userCompany !== 'QX' ? fleetEventType : "", 
      eventTypeCategoryTo: isAirborne ? fleetEventTypeCategory : "",
      adviseTimeUTC: editOtsData.advtime?.toISOString(),
      estimatedReturnToServiceTimeUTC: null,
      outOfServiceDateTimeUTC: existingOtsFleetData.OutOfServiceDateTimeUTC,
      ATACode: otsFleetDetailsData?.ataCode ?? "",
      ataCodeDescription: otsFleetDetailsData?.ataCodeDescription ?? "",
      hasPublicAttachment: otsFleetDetailsData?.hasPublicAttachment? otsFleetDetailsData?.hasPublicAttachment : existingOtsFleetData?.HasPublicAttachment ?? false,
      lastUpdatedBy: userData?.profile?.name ?? "",
    };
  };

  const confirmDetails = {
    title: `Create Events for Fleet ${selectedFleetType}`,
    message: "You are about to put multiple aircraft out of service at the same time. This action is irreversible and may impact fleet operations.<br/><br/><br/>Are you sure you want to continue?<br/><br/><br/>Once confirmed, you will not be able to cancel or undo this action",
    buttonText: "Yes, Put Fleet Out of Service"
  };

  const cancelDetails = {
    title: "Events Not Created ",
    message: "Are you sure you want to leave this page and go back to the dashboard? Some of the events in this fleet failed to create.",
    buttonText: "Yes"
  };

  const cancelBeforePost={
    title:`Cancel Event `,
    message:`Are you sure you want to cancel new event and go to the dashbaord?`,
    buttonText: "Yes"
  }

  return (
    <InlineNotificationProvider>
      <div className="ots-fleet-container">
        <div className="section section-1">
          <OTSFleetEventDetails
            onFleetTypeChange={handleFleetTypeChange}
            onOtsFleetDetailsChange={handleOtsFleetDetailsChange}
            isDisabled={overlay}
          />
        </div>
        {selectedFleetType && (
          <div className="section section-2">
            <FleetDetails
              fleetTypeSelection={selectedFleetType}
              onDataUpdate={handleDataUpdate}
              onValidDataShowing={handleValidDetailsCountShowing}
              inServiceAircraftData={fleetAircraftData}
            />
          </div>
        )}
      </div>
      <ConfirmModal
        message={confirmDetails.message}
        title={confirmDetails.title}
        buttonText={confirmDetails.buttonText}
        isOpen={showConfirmModal}
        close={handleConfirmClose}
        confirm={handleConfirmAction}
      />
      <CancelModal
        isOpen={showCancelModal}
        message={cancelDetails.message}
        title={cancelDetails.title}
        buttonText={cancelDetails.buttonText}
        close={handleKeepOnFleet}
        confirm={handleCancelEvent}
      />
      <CancelModal
        isOpen={showCancelModalBeforePost}
        message={cancelBeforePost.message}
        title={cancelBeforePost.title}
        buttonText={cancelBeforePost.buttonText}
        close={handleKeepEditing}
        confirm={handleCancelEvent}
      />

    </InlineNotificationProvider>
  );
};

export default React.memo(CreateOTSFleetEvent);